import { useCallback, useEffect, useState } from 'react';

import { Option } from '../OptionsList/OptionsList';
import { useMediaContext } from '../../../features/Media/providers/MediaContextProvider';
import { getDevices } from '../../../features/Media/services/media';
import { MediaDomainEvents } from '../../../features/Media/domain';
import { publish } from '../../../features/EventsBus';

export function useVideoDevicesOptions(): {
	opened: boolean;
	selected?: string;
	options: Option[];
	open: () => void;
	close: () => void;
	} {
	const [options, setOptions] = useState<Option[]>([]);
	const [opened, setOpened] = useState(false);
	const [selected, setSelected] = useState<string | undefined>(undefined);

	const { selectedVideoDevice } = useMediaContext();

	const loadOptions = async (): Promise<Option[]> => {
		const devices = await getDevices();

		if (selectedVideoDevice) {
			setSelected(selectedVideoDevice);
		}
		else if (devices?.videoinput.length && !selectedVideoDevice) {
			setSelected(devices.videoinput[0].deviceId);
		}

		return devices?.videoinput.length
			? devices.videoinput.map((device) => {
				if (device.deviceId === 'default' && !selectedVideoDevice) {
					setSelected(device.deviceId);
				}

				return {
					key: device.deviceId,
					label: device.label,
					onClick: () => {
						onSelectedDevice(device);
					},
				};
			  })
			: [];
	};

	const onSelectedDevice = async (device: MediaDeviceInfo) => {
		if (!device?.deviceId) {
			return;
		}

		publish<{ deviceId: string }>(
			MediaDomainEvents.changeCamDevice, {
				deviceId: device.deviceId,
			}
		);
	};

	useEffect(() => {
		if (selectedVideoDevice) {
			setSelected(selectedVideoDevice);
		}
	}, [selectedVideoDevice]);


	const open = useCallback(() => {
		setOpened(true);
	}, []);

	const close = useCallback(() => {
		setOpened(false);
	}, []);

	useEffect(() => {
		loadOptions()
			.then((devices) => {
				setOptions(devices);
			})
			.catch(() => {
				publish(MediaDomainEvents.cannotListVideoInputDevices);
			});
	}, []);

	return {
		options,
		opened,
		selected,
		open,
		close,
	};
}
